import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Button from '../Button/Button';
import InputField from '../InputField/InputField';
import SelectOptions from '../SelectOptions/SelectOptions';

import BackIcon from '../../assets/svgs/BackIcon';
import ImagePlaceHolderIcon from '../../assets/svgs/ImagePlaceHolderIcon';
import PlusIcon from '../../assets/svgs/PlusIcon';

import Api from '../../libs/Api';
import translate from '../../libs/i18n';
import emailRegex from '../../utils/emailRegex';
import onlyNumbers from '../../utils/onlyNumbers';
import { institutionShape } from '../../utils/propTypesShapes';
import { useIsMounted } from '../../utils/useIsMounted';

import { EditInstitutionFormStyle } from './EditInstitutionFormStyle';

const EditInstitutionForm = ({
  institutionToEdit,
  openEditInstitutionForm,
  setActionComplete,
  setLoading,
  setOpenEditInstitutionForm,
}) => {
  const inputFileRef = useRef();
  const isMounted = useIsMounted();
  const language = useSelector((state) => state.settings.language);

  const [address, setAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  const [adminAddress, setAdminAddress] = useState('');
  const [adminCity, setAdminCity] = useState('');
  const [adminComplement, setAdminComplement] = useState('');
  const [adminCpf, setAdminCpf] = useState('');
  const [adminCpfError, setAdminCpfError] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminNumber, setAdminNumber] = useState('');
  const [adminTelephone, setAdminTelephone] = useState('');
  const [city, setCity] = useState('');
  const [cityError, setCityError] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [cnpjError, setCnpjError] = useState('');
  const [code, setCode] = useState('');
  const [complement, setComplement] = useState('');
  const [complementError, setComplementError] = useState('');
  const [country, setCountry] = useState('');
  const [countryError, setCountryError] = useState('');
  const [emailAdminError, setEmailAdminError] = useState('');
  const [image, setImage] = useState();
  const [imageError, setImageError] = useState('');
  const [name, setName] = useState('');
  const [nameAdminError, setNameAdminError] = useState('');
  const [nameError, setNameError] = useState('');
  const [number, setNumber] = useState('');
  const [numberError, setNumberError] = useState('');
  const [responsibleError, setResponsibleError] = useState('');
  const [responsibles, setResponsibles] = useState([]);
  const [responsiblesOptions, setUserOptions] = useState([]);
  const [selectAdminError, setSelectAdminError] = useState('');
  const [selectedResponsibleId, setSelectedResponsibleId] = useState('');
  const [showAdminForm, setShowAdminForm] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [zipCodeError, setZipCodeError] = useState('');

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      setLoading(true);

      const response = await Api.getResponsibleThatAreNotAdminUser();

      setResponsibles(response?.data || []);
      setLoading(false);
    })();
  }, [isMounted, setLoading]);

  useEffect(() => {
    (async () => {
      const current = [
        {
          key: 0,
          value: '',
          text: translate('selectAdministrator', language),
        },
      ];

      if (responsibles?.length) {
        responsibles.forEach((user) => {
          if (user?.id && user?.name?.length) {
            current.push({
              key: user?.id,
              value: user?.id,
              text: user.name,
            });
          }
        });
      }

      if (
        institutionToEdit?.admin &&
        !current.some((option) => option?.key === institutionToEdit?.admin)
      ) {
        setLoading(true);

        const response = await Api.getUser(institutionToEdit?.admin);

        if (response?.name?.length) {
          current.push({
            key: institutionToEdit?.admin,
            value: institutionToEdit?.admin,
            text: response?.name,
          });
        }

        setSelectedResponsibleId(institutionToEdit?.admin);
        setLoading(false);
      }

      setUserOptions(current);
    })();
  }, [institutionToEdit?.admin, language, responsibles, setLoading]);

  useEffect(() => {
    if (selectedResponsibleId) {
      setShowAdminForm(false);
      setAdminName('');
      setAdminCpf('');
      setAdminAddress('');
      setAdminEmail('');
      setAdminTelephone('');
      setAdminCity('');
      setAdminComplement('');
      setCode('');
      setAdminNumber('');
      setSelectAdminError('');
    }
  }, [selectedResponsibleId]);

  useEffect(() => {
    setImage(institutionToEdit?.image || '');
    setName(institutionToEdit?.name || '');
    setCnpj(institutionToEdit?.cnpj || '');
    setZipCode(institutionToEdit?.post_code || '');
    setAddress(institutionToEdit?.address || '');
    setNumber(institutionToEdit?.number || '');
    setComplement(institutionToEdit?.complement || '');
    setCity(institutionToEdit?.city || '');
    setCountry(institutionToEdit?.country || '');
  }, [institutionToEdit]);

  const isHeadquarters = useMemo(
    () => institutionToEdit?.is_headquarters || false,
    [institutionToEdit?.is_headquarters]
  );

  const avatarImage = useMemo(() => {
    if (image && typeof image === 'string') {
      return image;
    }

    if (image) {
      return URL.createObjectURL(image);
    }

    return null;
  }, [image]);

  const addNewResponsible = useMemo(
    () => !selectedResponsibleId && showAdminForm,
    [selectedResponsibleId, showAdminForm]
  );

  const onClickEditInstitutionResponsible = () => {
    setSelectedResponsibleId('');
    setShowAdminForm(true);
  };

  const onChangeInstitutionImage = (event) => {
    if (
      event &&
      event.target &&
      event.target.files &&
      event.target.files.length
    ) {
      if (event.target.files.length > 1) {
        setImageError('Você só pode inserir uma única imagem');
      } else {
        const currentImageFile = event.target.files[0];
        setImage(currentImageFile);
      }
    }

    inputFileRef.current.files = null;
  };

  const validateData = () => {
    let hasError = false;

    if (!name?.length) {
      setNameError('theFieldIsRequired');
      hasError = true;
    } else if (name?.length > 255) {
      setNameError('invalidFieldMaximum250Characters');
      hasError = true;
    }

    if (!cnpj?.length) {
      setCnpjError('theFieldIsRequired');
      hasError = true;
    } else if (cnpj?.length !== 14) {
      setCnpjError('invalidFieldMustHave14Numbers');
      hasError = true;
    }

    if (zipCode?.length > 8) {
      setZipCodeError('invalidFieldMustHave8Numbers');
      hasError = true;
    }

    if (address?.length > 255) {
      setAddressError('invalidFieldMaximum250Characters');
      hasError = true;
    }

    if (number?.length > 30) {
      setNumberError('invalidField');
      hasError = true;
    }

    if (complement?.length > 255) {
      setComplementError('invalidFieldMaximum250Characters');
      hasError = true;
    }

    if (city?.length > 255) {
      setCityError('invalidFieldMaximum250Characters');
      hasError = true;
    }

    if (country?.length > 50) {
      setCountryError('invalidFieldMaximum50Characters');
      hasError = true;
    }

    if (addNewResponsible) {
      if (!adminName?.length) {
        setNameAdminError('theFieldIsRequired');
        hasError = true;
      } else if (adminName?.length > 255) {
        setNameAdminError('invalidFieldMaximum250Characters');
        hasError = true;
      }

      if (adminCpf?.length !== 11) {
        setAdminCpfError('invalidFieldMustHave11Numbers');
        hasError = true;
      }

      if (!adminEmail?.length) {
        setEmailAdminError('theFieldIsRequired');
        hasError = true;
      } else if (!emailRegex.test(adminEmail.toLowerCase())) {
        setEmailAdminError('invalidField');
        hasError = true;
      }
    }

    if (!addNewResponsible && !selectedResponsibleId) {
      setResponsibleError('theFieldIsRequired');
    }

    return hasError;
  };

  const onCloseEditInstitutionForm = () => {
    setAddress('');
    setAdminAddress('');
    setAdminCity('');
    setAdminComplement('');
    setAdminCpf('');
    setAdminEmail('');
    setAdminName('');
    setAdminNumber('');
    setAdminTelephone('');
    setCity('');
    setCnpj('');
    setCode('');
    setComplement('');
    setCountry('');
    setEmailAdminError('');
    setName('');
    setNameAdminError('');
    setNumber('');
    setOpenEditInstitutionForm('');
    setSelectAdminError('');
    setZipCode('');
  };

  const onClickEditInstitution = async () => {
    setLoading(true);

    const hasError = validateData();
    let hasAdmin = false;

    if (hasError) {
      return setLoading(false);
    }

    const editInstitution = new FormData();

    if (name?.length) {
      editInstitution.append('name', name);
    }

    if (cnpj?.length) {
      editInstitution.append('cnpj', cnpj);
    }

    if (zipCode?.length) {
      editInstitution.append('post_code', zipCode);
    }

    if (image) {
      editInstitution.append('image', image);
    }

    if (address?.length) {
      editInstitution.append('address', address);
    }

    if (number?.length) {
      editInstitution.append('number', number);
    }

    if (complement?.length) {
      editInstitution.append('complement', complement);
    }

    if (city?.length) {
      editInstitution.append('city', city);
    }

    if (country?.length) {
      editInstitution.append('country', country);
    }

    if (selectedResponsibleId) {
      editInstitution.append('admin', selectedResponsibleId);
      hasAdmin = true;
    }

    if (adminName?.length && adminEmail?.length) {
      const adminToAdd = {
        name: adminName,
        email: adminEmail,
        password: 'zzz%*&*@)()*@!@##*@(',
        accepts_usage_terms: true,
        accepts_privacy_policy: true,
        type: 'RES',
      };

      if (adminCpf?.length) {
        adminToAdd.cpf = adminCpf;
      }

      if (adminTelephone?.length) {
        adminToAdd.phone = adminTelephone;
      }

      const registerUserResponse = await Api.registerUser();

      if (
        registerUserResponse?.status === 201 &&
        registerUserResponse?.data?.id
      ) {
        editInstitution.append('admin', registerUserResponse.data.id);
        hasAdmin = true;

        setActionComplete({
          type: 'success',
          text: 'Dados alterados com sucesso',
        });
      } else {
        setActionComplete({
          type: 'error',
          text: 'Ocorreu um erro ao adicionar um novo administrador',
        });

        return setLoading(false);
      }
    }

    if (hasAdmin) {
      const editInstitutionResponse = await Api.editInstitution(
        institutionToEdit.id,
        editInstitution
      );

      if (editInstitutionResponse?.status === 200) {
        setActionComplete({
          type: 'success',
          text: translate('editSucess',language),
        });
        onCloseEditInstitutionForm();
      } else {
        setActionComplete({
          type: 'error',
          text: 'Error ao editar instituição',
        });
      }
    }

    return setLoading(false);
  };

  const handleChangeSelect = (value, options, setValue) => {
    const currentOption = options.find(
      (option) => `${option.value}` === `${value}`
    );

    if (currentOption) {
      setValue(currentOption.value);
    }
  };

  return (
    <EditInstitutionFormStyle
      className={`edit-institution-form${openEditInstitutionForm}`}
    >
      <div className="back-button-container">
        <button
          type="button"
          className="back"
          onClick={onCloseEditInstitutionForm}
        >
          <BackIcon />
          <span>{translate('back', language)}</span>
        </button>
      </div>
      <div className="header-form">
        <h1 className="title-header">{`${translate('edit', language)} ${
          language === 'pt-br'
            ? `${translate('informations', language)} ${name}`
            : `${name} ${translate('information', language)}`
        }`}</h1>
      </div>
      <div className="logo-container">
        <span className="image-label">{translate('logoImage', language)}</span>
        <div
          className="logo"
          style={{
            backgroundImage: avatarImage?.length ? `url(${avatarImage})` : '',
          }}
        >
          {!avatarImage?.length ? <ImagePlaceHolderIcon /> : null}
          <span className="add">
            <PlusIcon />
          </span>
          <input
            ref={inputFileRef}
            type="file"
            className="input-file"
            accept="*/png,.png, image/png, */png, .png, */jpeg,.jpeg, image/jpeg, */jpeg, .jpeg"
            multiple
            onChange={onChangeInstitutionImage}
          />
        </div>
        {imageError ? <p className="error-message">{imageError}</p> : null}
        <span className="add-logo">{translate('addLogo', language)}</span>
      </div>
      <div className="data-column">
        <div className="institution-data-form">
          <h2 className="subtitle-institution-form">
            {language === 'pt-br'
              ? `${translate('data', language)} ${name}`
              : `${name} ${translate('data', language)}`}
          </h2>
          <div className="input-label-wrapper">
            <span className="label-wrapper">
              {translate('name', language)}
              <span className="required-field">*</span>
            </span>
            <div className="input-element-wrapper">
              <InputField
                type="text"
                inputName="name"
                value={name}
                noMarginBottom
                onChange={(nameValue) => {
                  if (nameError?.length) {
                    setNameError('');
                  }

                  setName(nameValue);
                }}
                placeholder={translate('enterInstitutionName', language)}
                error={
                  nameError?.length ? `${translate(nameError, language)}.` : ''
                }
              />
            </div>
          </div>
          <div className="input-label-wrapper">
            <span className="label-wrapper">
              CNPJ
              <span className="required-field">*</span>
            </span>
            <div className="input-element-wrapper">
              <InputField
                type="text"
                inputName="matriculation"
                value={cnpj}
                noMarginBottom
                onChange={(cnpjValue) => {
                  if (cnpjError?.length) {
                    setCnpjError('');
                  }

                  setCnpj(onlyNumbers(cnpjValue));
                }}
                placeholder="00.000.000/0001-00 "
                error={
                  cnpjError?.length ? `${translate(cnpjError, language)}.` : ''
                }
              />
            </div>
          </div>
          <div className="input-label-wrapper">
            <span className="label-wrapper">
              {translate('zipCode', language)}
            </span>
            <div className="input-element-wrapper">
              <InputField
                type="text"
                inputName="zipCode"
                value={zipCode}
                noMarginBottom
                onChange={(zipCodeValue) => {
                  if (zipCodeError?.length) {
                    setZipCodeError('');
                  }

                  setZipCode(onlyNumbers(zipCodeValue));
                }}
                placeholder="00000-000"
                error={
                  zipCodeError?.length
                    ? `${translate(zipCodeError, language)}.`
                    : ''
                }
              />
            </div>
          </div>
          <div className="input-label-wrapper">
            <span className="label-wrapper">
              {translate('address', language)}
            </span>
            <div className="input-element-wrapper">
              <InputField
                type="text"
                inputName="address"
                value={address}
                noMarginBottom
                onChange={(addressValue) => {
                  if (addressError?.length) {
                    setAddressError('');
                  }

                  setAddress(addressValue);
                }}
                placeholder={translate('enterInstitutionAddress', language)}
                error={
                  addressError?.length
                    ? `${translate(addressError, language)}.`
                    : ''
                }
              />
            </div>
          </div>
          <div className="side-by-side-inputs">
            <div className="input-label-wrapper">
              <span className="label-wrapper">
                {translate('number', language)}
              </span>
              <div className="input-element-wrapper">
                <InputField
                  type="text"
                  inputName="number"
                  value={number}
                  noMarginBottom
                  onChange={(numberValue) => {
                    if (numberError?.length) {
                      setNumberError('');
                    }

                    setNumber(onlyNumbers(numberValue));
                  }}
                  error={
                    numberError?.length
                      ? `${translate(numberError, language)}.`
                      : ''
                  }
                />
              </div>
            </div>
            <div className="input-label-wrapper">
              <span className="label-wrapper">
                {translate('complement', language)}
              </span>
              <div className="input-element-wrapper">
                <InputField
                  type="text"
                  inputName="complement"
                  value={complement}
                  noMarginBottom
                  onChange={(complementValue) => {
                    if (complementError?.length) {
                      setComplementError('');
                    }

                    setComplement(complementValue);
                  }}
                  error={
                    complementError?.length
                      ? `${translate(complementError, language)}.`
                      : ''
                  }
                />
              </div>
            </div>
          </div>
          <div className="side-by-side-inputs">
            <div className="input-label-wrapper">
              <span className="label-wrapper">
                {translate('city', language)}
              </span>
              <div className="input-element-wrapper">
                <InputField
                  type="text"
                  inputName="city"
                  value={city}
                  noMarginBottom
                  onChange={(cityValue) => {
                    if (complementError?.length) {
                      setCityError('');
                    }

                    setCity(cityValue);
                  }}
                  error={
                    cityError?.length
                      ? `${translate(cityError, language)}.`
                      : ''
                  }
                />
              </div>
            </div>
            <div className="input-label-wrapper">
              <span className="label-wrapper">
                {translate('country', language)}
              </span>
              <div className="input-element-wrapper">
                <InputField
                  type="text"
                  inputName="country"
                  value={country}
                  noMarginBottom
                  onChange={(countryValue) => {
                    if (countryError?.length) {
                      setCountryError('');
                    }

                    setCountry(countryValue);
                  }}
                  error={
                    countryError?.length
                      ? `${translate(countryError, language)}.`
                      : ''
                  }
                />
              </div>
            </div>
          </div>
          {!isHeadquarters ? (
            <>
              <div className="input-label-form select-add-responsible">
                <span className="label">
                  {translate('administrator', language)}
                </span>
                <div className="input">
                  <SelectOptions
                    theme="grayish-blue-outline"
                    id="role"
                    options={responsiblesOptions}
                    value={selectedResponsibleId}
                    onChange={(value) => {
                      setResponsibleError('');
                      handleChangeSelect(
                        value,
                        responsiblesOptions,
                        setSelectedResponsibleId
                      );
                    }}
                    error={responsibleError}
                  />
                  {selectAdminError ? (
                    <p className="error-message">{selectAdminError}</p>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                className="add-responsible"
                onClick={onClickEditInstitutionResponsible}
              >
                {translate('addANewAdministrator', language)}
              </button>
            </>
          ) : null}
        </div>
        {!isHeadquarters && showAdminForm ? (
          <div className="administrator-form">
            <h2 className="subtitle-institution-form">
              {translate('administratorData', language)}
            </h2>
            <span className="text-form">
              {`${translate('youCanEditAdminInfoBelow', language)}. ${translate(
                'ifYouWantReplaceMainAccountAdminContactUs',
                language
              )}.`}
            </span>
            <div className="input-label-wrapper">
              <span className="label-wrapper">
                {translate('name', language)}
                <span className="required-field">*</span>
              </span>
              <div className="input-element-wrapper">
                <InputField
                  type="text"
                  inputName="adminName"
                  value={adminName}
                  noMarginBottom
                  onChange={(adminNameValue) => {
                    if (nameAdminError?.length) {
                      setNameAdminError('');
                    }

                    setAdminName(adminNameValue);
                  }}
                  error={
                    nameAdminError?.length
                      ? `${translate(nameAdminError, language)}.`
                      : ''
                  }
                />
              </div>
            </div>
            <div className="input-label-wrapper">
              <span className="label-wrapper">
                CPF
                <span className="required-field">*</span>
              </span>
              <div className="input-element-wrapper">
                <InputField
                  type="text"
                  inputName="adminCpf"
                  value={adminCpf}
                  noMarginBottom
                  placeholder="000.000.000-00"
                  onChange={(adminCpfValue) => {
                    if (adminCpfError?.length) {
                      setAdminCpfError('');
                    }

                    setAdminCpf(onlyNumbers(adminCpfValue));
                  }}
                  error={
                    adminCpfError?.length
                      ? `${translate(adminCpfError, language)}.`
                      : ''
                  }
                />
              </div>
            </div>
            <div className="input-label-wrapper">
              <span className="label-wrapper">
                E-mail
                <span className="required-field">*</span>
              </span>
              <div className="input-element-wrapper">
                <InputField
                  type="text"
                  inputName="adminEmail"
                  value={adminEmail}
                  noMarginBottom
                  placeholder={translate('exampleEmail', language)}
                  onChange={(adminEmailValue) => {
                    if (emailAdminError?.length) {
                      setEmailAdminError('');
                    }

                    setAdminEmail(adminEmailValue);
                  }}
                  error={
                    emailAdminError?.length
                      ? `${translate(emailAdminError, language)}.`
                      : ''
                  }
                />
              </div>
            </div>
            <div className="select-label-wrapper">
              <span className="label-wrapper">
                {translate('telephone', language)}
              </span>
              <div className="select-input-wrapper">
                <div className="select-wrapper">
                  <SelectOptions
                    theme="soft-blue-outline"
                    name="role"
                    id="role"
                    options={[
                      {
                        key: 0,
                        value: '',
                        text: '+00',
                      },
                      { key: 1, value: '+55', text: '+55' },
                      { key: 2, value: '+33', text: '+33' },
                    ]}
                    value={code}
                    onChange={(value) =>
                      handleChangeSelect(
                        value,
                        [
                          {
                            key: 0,
                            value: '',
                            text: '+00',
                          },
                          { key: 1, value: '+55', text: '+55' },
                          { key: 2, value: '+33', text: '+33' },
                        ],
                        setCode
                      )
                    }
                  />
                </div>
                <div className="input-element-wrapper">
                  <InputField
                    type="text"
                    inputName="adminTelephone"
                    value={adminTelephone}
                    noMarginBottom
                    onChange={setAdminTelephone}
                    placeholder="( 00 ) 9 0000-0000"
                  />
                </div>
              </div>
            </div>
            <div className="input-label-wrapper">
              <span className="label-wrapper">
                {translate('address', language)}
              </span>
              <div className="input-element-wrapper">
                <InputField
                  type="text"
                  inputName="adminAddress"
                  value={adminAddress}
                  noMarginBottom
                  onChange={setAddress}
                />
              </div>
            </div>
            <div className="side-by-side-inputs">
              <div className="input-label-wrapper">
                <span className="label-wrapper">
                  {translate('number', language)}
                </span>
                <div className="input-element-wrapper">
                  <InputField
                    type="text"
                    inputName="adminNumber"
                    value={adminNumber}
                    noMarginBottom
                    onChange={setAdminNumber}
                  />
                </div>
              </div>
              <div className="input-label-wrapper">
                <span className="label-wrapper">
                  {translate('complement', language)}
                </span>
                <div className="input-element-wrapper">
                  <InputField
                    type="text"
                    inputName="adminComplement"
                    value={adminComplement}
                    noMarginBottom
                    onChange={setAdminComplement}
                  />
                </div>
              </div>
            </div>
            <div className="side-by-side-inputs">
              <div className="input-label-wrapper">
                <span className="label-wrapper">
                  {translate('city', language)}
                </span>
                <div className="input-element-wrapper">
                  <InputField
                    type="text"
                    inputName="adminCity"
                    value={adminCity}
                    noMarginBottom
                    onChange={setAdminCity}
                  />
                </div>
              </div>
              <div className="input-label-wrapper">
                <span className="label-wrapper">
                  {translate('country', language)}
                </span>
                <div className="input-element-wrapper">
                  <InputField
                    type="text"
                    inputName="country"
                    value={country}
                    noMarginBottom
                    onChange={setCountry}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {isHeadquarters ? (
          <div className="healthscan-plan-form">
            <h2 className="subtitle-institution-form">
              {translate('healthscanPlan', language)}
            </h2>
            <span className="text-form">
              {`${translate(
                'theseAreInfoYourHealthscanPlan',
                language
              )}. ${translate(
                'ifYouWantChangeItPleaseGetTouchWithUs',
                language
              )}.`}
            </span>
            {institutionToEdit?.institutionplan?.pathology_name?.length ? (
              <div className="input-label-wrapper">
                <span className="label-wrapper">
                  {translate('pathology', language)}
                </span>
                <div className="input-element-wrapper">
                  <InputField
                    type="text"
                    inputName="pathology"
                    value={institutionToEdit?.institutionplan?.pathology_name}
                    onChange={() => {}}
                    noMarginBottom
                    disabled
                  />
                </div>
              </div>
            ) : null}
            {institutionToEdit?.institutionplan?.exams_per_month?.length ? (
              <div className="input-label-wrapper">
                <span className="label-wrapper">
                  {translate('numberOfExams', language)}
                </span>
                <div className="input-element-wrapper">
                  <InputField
                    type="text"
                    inputName="numberOfExams"
                    value={institutionToEdit?.institutionplan?.exams_per_month}
                    noMarginBottom
                    onChange={() => {}}
                    disabled
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="buttons-container">
          <div className="button-wrapper">
            <Button
              type="button"
              theme="default"
              onClick={onCloseEditInstitutionForm}
              text={translate('cancel', language).toUpperCase()}
            />
          </div>
          <div className="button-wrapper">
            <Button
              type="button"
              theme="default"
              onClick={() => onClickEditInstitution(institutionToEdit.id)}
              text={translate('confirm', language).toUpperCase()}
            />
          </div>
        </div>
      </div>
    </EditInstitutionFormStyle>
  );
};

EditInstitutionForm.propTypes = {
  openEditInstitutionForm: PropTypes.string,
  setOpenEditInstitutionForm: PropTypes.func.isRequired,
  setActionComplete: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  institutionToEdit: institutionShape.isRequired,
};

EditInstitutionForm.defaultProps = {
  openEditInstitutionForm: '',
};

export default EditInstitutionForm;
